// Targets the agentic-backend v1 trigger control plane (`target: 'workflow'`).
// Open to any org member; a connection is private to whoever connected it, and
// a trigger on someone else's connection comes back with its config redacted.
// See src/api/v1/routers/triggers.py.
import { apiRequest } from '@/utils/api-request';
import {
  AuthInitiateResult,
  ConnectionHealthResult,
  CreateConnectionInput,
  CreateSubscriptionInput,
  CreateTriggerInput,
  DriveFolderPage,
  TestFireResult,
  Trigger,
  TriggerConnection,
  TriggerSubscription,
} from './types';

const base = (orgId: string) => `/api/v1/orgs/${orgId}`;

const failed = (response: { detail?: string }, fallback: string): Error =>
  new Error(response.detail || fallback);

export const listTriggerConnections = async (
  orgId: string,
): Promise<TriggerConnection[]> => {
  const response = await apiRequest<TriggerConnection[]>({
    url: `${base(orgId)}/trigger-connections`,
    method: 'GET',
    target: 'workflow',
  });
  if (response?.detail) throw failed(response, 'Could not load connections');
  return response?.data ?? [];
};

export const createTriggerConnection = async (
  orgId: string,
  input: CreateConnectionInput,
): Promise<TriggerConnection> => {
  const response = await apiRequest<TriggerConnection>({
    url: `${base(orgId)}/trigger-connections`,
    method: 'POST',
    target: 'workflow',
    body: {
      provider: 'google_drive',
      auth_backend: 'managed',
      ...input,
    } as unknown as Record<string, unknown>,
  });
  if (response?.detail || !response?.data) {
    throw failed(response ?? {}, 'Could not start Google Drive authorization');
  }
  return response.data;
};

export const initiateConnectionAuth = async (
  orgId: string,
  connectionId: string,
  popup = false,
): Promise<AuthInitiateResult> => {
  const response = await apiRequest<AuthInitiateResult>({
    url: `${base(orgId)}/trigger-connections/${connectionId}/auth:initiate`,
    method: 'POST',
    target: 'workflow',
    body: { popup },
  });
  if (response?.detail || !response?.data) {
    throw failed(response ?? {}, 'Could not restart Google authorization');
  }
  return response.data;
};

export const checkConnectionHealth = async (
  orgId: string,
  connectionId: string,
): Promise<ConnectionHealthResult> => {
  const response = await apiRequest<ConnectionHealthResult>({
    url: `${base(orgId)}/trigger-connections/${connectionId}/health`,
    method: 'POST',
    target: 'workflow',
  });
  if (response?.detail || !response?.data) {
    throw failed(response ?? {}, 'Could not check the connection');
  }
  return response.data;
};

export const deleteTriggerConnection = async (
  orgId: string,
  connectionId: string,
): Promise<void> => {
  const response = await apiRequest({
    url: `${base(orgId)}/trigger-connections/${connectionId}`,
    method: 'DELETE',
    target: 'workflow',
  });
  if (response?.detail)
    throw failed(response, 'Could not remove the connection');
};

export const listDriveFolders = async (
  orgId: string,
  connectionId: string,
  params: { q?: string; pageSize?: number } = {},
): Promise<DriveFolderPage> => {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.pageSize) query.set('page_size', String(params.pageSize));
  const suffix = query.toString() ? `?${query}` : '';
  const response = await apiRequest<DriveFolderPage>({
    url: `${base(orgId)}/trigger-connections/${connectionId}/folders${suffix}`,
    method: 'GET',
    target: 'workflow',
    // A connection awaiting consent 409s; that is a state, not an incident.
    suppressSentryStatuses: [409],
  });
  if (response?.detail) throw failed(response, 'Could not load Drive folders');
  return response?.data ?? { folders: [] };
};

export const listTriggers = async (
  orgId: string,
  workflowId?: string,
): Promise<Trigger[]> => {
  const response = await apiRequest<Trigger[]>({
    url: workflowId
      ? `${base(orgId)}/triggers?workflow_id=${encodeURIComponent(workflowId)}`
      : `${base(orgId)}/triggers`,
    method: 'GET',
    target: 'workflow',
  });
  if (response?.detail) throw failed(response, 'Could not load triggers');
  return response?.data ?? [];
};

export const createTrigger = async (
  orgId: string,
  input: CreateTriggerInput,
): Promise<Trigger> => {
  const response = await apiRequest<Trigger>({
    url: `${base(orgId)}/triggers`,
    method: 'POST',
    target: 'workflow',
    body: input as unknown as Record<string, unknown>,
    // 422 carries the actionable reason (e.g. a Shared Drive folder).
    suppressSentryStatuses: [422],
  });
  if (response?.detail || !response?.data) {
    throw failed(response ?? {}, 'Could not create the trigger');
  }
  return response.data;
};

export const setTriggerEnabled = async (
  orgId: string,
  triggerId: string,
  enabled: boolean,
): Promise<void> => {
  const action = enabled ? 'enable' : 'disable';
  const response = await apiRequest({
    url: `${base(orgId)}/triggers/${triggerId}/${action}`,
    method: 'POST',
    target: 'workflow',
  });
  if (response?.detail)
    throw failed(response, `Could not ${action} the trigger`);
};

export const deleteTrigger = async (
  orgId: string,
  triggerId: string,
): Promise<void> => {
  const response = await apiRequest({
    url: `${base(orgId)}/triggers/${triggerId}`,
    method: 'DELETE',
    target: 'workflow',
  });
  if (response?.detail) throw failed(response, 'Could not delete the trigger');
};

export const testFireTrigger = async (
  orgId: string,
  triggerId: string,
): Promise<TestFireResult> => {
  const response = await apiRequest<TestFireResult>({
    url: `${base(orgId)}/triggers/${triggerId}/test-fire`,
    method: 'POST',
    target: 'workflow',
  });
  if (response?.detail || !response?.data) {
    throw failed(response ?? {}, 'Test run failed');
  }
  return response.data;
};

export const listTriggerSubscriptions = async (
  orgId: string,
  triggerId: string,
): Promise<TriggerSubscription[]> => {
  const response = await apiRequest<TriggerSubscription[]>({
    url: `${base(orgId)}/triggers/${triggerId}/subscriptions`,
    method: 'GET',
    target: 'workflow',
  });
  if (response?.detail) throw failed(response, 'Could not load workflows');
  return response?.data ?? [];
};

export const createTriggerSubscription = async (
  orgId: string,
  triggerId: string,
  input: CreateSubscriptionInput,
): Promise<TriggerSubscription> => {
  const response = await apiRequest<TriggerSubscription>({
    url: `${base(orgId)}/triggers/${triggerId}/subscriptions`,
    method: 'POST',
    target: 'workflow',
    body: {
      target_kind: 'workflow_run',
      input_binding: [],
      version_policy: { mode: 'latest_published' },
      ...input,
    } as unknown as Record<string, unknown>,
    // 422 means the workflow has zero or several file-input nodes.
    suppressSentryStatuses: [422],
  });
  if (response?.detail || !response?.data) {
    throw failed(response ?? {}, 'Could not attach the workflow');
  }
  return response.data;
};

export const deleteTriggerSubscription = async (
  orgId: string,
  triggerId: string,
  subscriptionId: string,
): Promise<void> => {
  const response = await apiRequest({
    url: `${base(orgId)}/triggers/${triggerId}/subscriptions/${subscriptionId}`,
    method: 'DELETE',
    target: 'workflow',
  });
  if (response?.detail) throw failed(response, 'Could not detach the workflow');
};
